import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ThemePalette } from '@angular/material/core';
import { Conversation, Result, Seller } from '../../reservation/reservation.model';
import { MessageService } from '../message.service';
import { FormControlConfig, NewFilters } from '../../common/new-filters';
import { FormBuilder, Validators } from '@angular/forms';
import { DataBagService } from '../../common/data-bag.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { from, map, merge, Observable, of, startWith, Subscription, switchMap } from 'rxjs';
import { DatePipe } from '@angular/common';
import { SelectionModel } from '@angular/cdk/collections';
import { MatPaginator } from '@angular/material/paginator';

@Component({
	selector: 'app-message-filters',
	templateUrl: './message-filters.component.html',
	styleUrls: ['./message-filters.component.scss'],
})
export class MessageFiltersComponent extends NewFilters implements OnInit, OnDestroy, AfterViewInit {
	constructor(
		protected service: MessageService,
		protected formBuilder: FormBuilder,
		protected api: DataBagService,
		protected route: ActivatedRoute,
		protected router: Router,
		protected datePipe: DatePipe,
	) {
		super(formBuilder, api, route, router, datePipe);
	}

	@Input() selection: SelectionModel<Conversation>;
	@Input() paginator: MatPaginator;
	protected checkboxColor: ThemePalette = 'primary';
	protected sellers: Seller[];
	controls: FormControlConfig[] = [
		{
			name: 'table_id',
		},
		{
			name: 'table_name',
		},
		// {
		// 	name: 'seller',
		// },
		{
			name: 'email',
			validators: [Validators.pattern(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/)],
		},
		{
			name: 'not_assigned',
		},
		{ name: 'labels__contains' },
		{
			name: 'watching',
		},
		{
			name: 'q',
		},
	];
	url = '/conversation/threads/';
	parameters = { labels__exclude: 'archived' };
	protected filtersLoading = false;
	private filtersSubscription: Subscription;
	private paginationSubscription: Subscription;

	async ngOnInit() {
		this.createFormGroup();
		if (!!this.filtersFormGroup.get('labels__contains').value) {
			delete this.parameters.labels__exclude;
		}
		this.sellers = await this.service.getSellers();
		this.getDataSource();
	}

	ngAfterViewInit() {
		this.filtersSubscription = this.service.setFilters.subscribe(async (value) => {
			if (value) this.service.clearCache();
			this.paginationSubscription?.unsubscribe();
			this.getDataSource();
			this.filtersLoading = false;
		});
	}

	private getDataSource() {
		this.paginationSubscription = merge(this.paginator.page)
			.pipe(
				startWith({}),
				switchMap(() => this.getDataObservable()),
				map((data: Result<Conversation>) => this.handleDataResponse(data)),
			)
			.subscribe((data: Conversation[]) => {
				this.service.conversations.data = data;
				if (this.service.previousSelection.length) {
					this.service.setSelection.next(this.service.previousSelection);
				}
			});
	}

	private getDataObservable(): Observable<Result<Conversation>> {
		this.parameters['limit'] = this.paginator.pageSize;
		this.parameters['offset'] = this.paginator.pageSize * this.paginator.pageIndex;
		const cachedData = this.getCachedData();
		return cachedData ? of(cachedData) : from(this.submitWithPagination());
	}

	private getCachedData(): Result<Conversation> {
		const cacheKey = this.buildFilterUrl;
		if (this.service.cache[cacheKey]) {
			this.service.activeCacheKey = cacheKey;
			this.setQueryParams(this.buildQueryParams);
			return this.service.cache[cacheKey];
		}
		return null;
	}

	private async submitWithPagination(): Promise<Result<Conversation>> {
		this.service.isLoadingResults = true;
		const data = await this.submit();
		const cacheKey = this.buildFilterUrl;
		const currentCache = { ...this.service.cache };
		currentCache[cacheKey] = data;
		this.service.activeCacheKey = cacheKey;
		this.service.cache = currentCache;
		this.service.isLoadingResults = false;
		return data;
	}

	private handleDataResponse(data: Result<Conversation>): Conversation[] {
		if (data === null) {
			return [];
		}
		this.service.resultsLength = data.count;
		return data.results;
	}

	displayFn(state: number): string {
		const sellerName = this.sellers?.find((seller) => seller.id === state)?.name;
		return sellerName ?? '';
	}

	protected updateFormControlCheckboxValue(event: MatCheckboxChange, value: boolean | string) {
		this.filtersFormGroup.get(event.source.name).setValue(event.checked ? value : '');
	}

	protected updateLabelsArchiveValue(checked: boolean) {
		if (checked) {
			this.filtersFormGroup.get('labels__contains').setValue('archived');
			delete this.parameters.labels__exclude;
		} else {
			this.filtersFormGroup.get('labels__contains').setValue('');
			this.parameters.labels__exclude = 'archived';
		}
	}

	protected beforeClearFilters() {
		if (!this.parameters.labels__exclude) {
			this.parameters.labels__exclude = 'archived';
		}
		this.clearFilters();
	}

	protected loadFilters() {
		this.filtersLoading = true;
		this.parameters['offset'] = 0;
		this.paginator.pageIndex = 0;
		this.service.setFilters.next(false);
	}

	ngOnDestroy() {
		this.paginationSubscription?.unsubscribe();
	}
}
